import React from 'react';
import { Button } from '../Button';
import { ThemeMode, useProviderTheme } from '../hooks';
import { Loader } from '../Loader';
import './CommunicationMessage.css';
import { ACTION_NEEDED_ILLUSTRATIONS } from './Illustrations/ActionNeeded';
import { CELEBRATION_ILLUSTRATIONS } from './Illustrations/Celebration';
import { COMPLETED_ILLUSTRATIONS } from './Illustrations/Completed';
import { EMPTY_ILLUSTRATIONS } from './Illustrations/Empty';
import { ERROR_ILLUSTRATIONS } from './Illustrations/Error';
import { HELLO_ILLUSTRATIONS } from './Illustrations/Hello';
import { PROGRESS_ILLUSTRATIONS } from './Illustrations/Progress';
import { WAITING_ILLUSTRATIONS } from './Illustrations/Waiting';
import { WELCOME_ILLUSTRATIONS } from './Illustrations/Welcome';

export enum Illustrations {
  Hello,
  Empty,
  Error,
  Completed,
  Waiting,
  Welcome,
  Celebration,
  Progress,
  ActionNeeded,
}

export enum IllustrationSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export interface CommunicationMessageAction {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  icon?: JSX.Element;
}

export interface CommunicationMessageProps {
  /**
   * Defines the illustration to be shown.
   *
   * @type {Illustrations}
   * @memberof CommunicationMessageProps
   */
  illustration: Illustrations;
  /**
   * Defines the size of the illustration to be shown.
   *
   * @type {IllustrationSize}
   * @memberof CommunicationMessageProps
   * @default IllustrationSize.Medium
   */
  illustrationSize?: IllustrationSize;
  headline: string;
  text: string;
  /**
   * Wether use dark version of illustration or not.
   * @deprecated Do not use this property anymore. It will no longer be respected. The component detects the correct them mode by itself.
   *
   * @type {boolean}
   * @memberof CommunicationMessageProps
   *
   */
  useDarkIllustration?: boolean;
  primaryAction?: CommunicationMessageAction;
  secondaryAction?: CommunicationMessageAction;
  dataCy?: string; // Used for Cypress testing descriptors
  dataTestId?: string; // Used for component testing descriptors
  /**
   * Shows loading spinner below the message text.
   */
  showLoader?: boolean;
}

export interface CommunicationIllustrationProps {
  DARK: React.ReactNode;
  LIGHT: React.ReactNode;
  CONTRAST?: React.ReactNode;
}

const getIllustrationKey = (
  mode: ThemeMode,
  illustration?: CommunicationIllustrationProps,
): keyof CommunicationIllustrationProps => {
  if (mode === ThemeMode.Light) {
    return 'LIGHT';
  }

  if (mode === ThemeMode.Dark) {
    return 'DARK';
  }

  return illustration?.CONTRAST ? 'CONTRAST' : 'DARK';
};

const toIllustrationClassName = (size: IllustrationSize) => {
  let illustration = 'mediumIllustration';
  switch (size) {
    case IllustrationSize.Small:
      illustration = 'smallIllustration';
      break;
    case IllustrationSize.Medium:
      illustration = 'mediumIllustration';
      break;
    case IllustrationSize.Large:
      illustration = 'largeIllustration';
      break;
  }

  return `communicationIllustrationWrapper ${illustration}`;
};

export const CommunicationMessage: React.FC<CommunicationMessageProps> = ({
  headline,
  illustration,
  illustrationSize = IllustrationSize.Medium,
  text,
  primaryAction,
  secondaryAction,
  dataCy,
  dataTestId,
  showLoader,
}) => {
  const { mode } = useProviderTheme();
  let illustrationComponent = null;

  switch (illustration) {
    case Illustrations.Empty:
      illustrationComponent = EMPTY_ILLUSTRATIONS;
      break;
    case Illustrations.Hello:
      illustrationComponent = HELLO_ILLUSTRATIONS;
      break;
    case Illustrations.Error:
      illustrationComponent = ERROR_ILLUSTRATIONS;
      break;
    case Illustrations.Completed:
      illustrationComponent = COMPLETED_ILLUSTRATIONS;
      break;
    case Illustrations.Waiting:
      illustrationComponent = WAITING_ILLUSTRATIONS;
      break;
    case Illustrations.Welcome:
      illustrationComponent = WELCOME_ILLUSTRATIONS;
      break;
    case Illustrations.Celebration:
      illustrationComponent = CELEBRATION_ILLUSTRATIONS;
      break;
    case Illustrations.Progress:
      illustrationComponent = PROGRESS_ILLUSTRATIONS;
      break;
    case Illustrations.ActionNeeded:
      illustrationComponent = ACTION_NEEDED_ILLUSTRATIONS;
  }

  const key = getIllustrationKey(mode, illustrationComponent);

  return (
    <div className="communicationCard" data-cy={dataCy} data-testid={dataTestId}>
      {illustrationComponent !== null && (
        <div className={toIllustrationClassName(illustrationSize)}>
          {illustrationComponent[key]}
        </div>
      )}
      {headline && <h1 className="communicationMessageHeadline">{headline}</h1>}
      <p className="communicationMessageText">{text}</p>

      {primaryAction && (
        <Button
          appearance="primary"
          className="communicationMessageAction"
          onClick={primaryAction.onClick}
          loading={primaryAction.loading}
          disabled={primaryAction.disabled}
          icon={primaryAction.icon}
        >
          {primaryAction.label}
        </Button>
      )}
      {secondaryAction && (
        <Button
          appearance="transparent"
          className="communicationMessageAction"
          onClick={secondaryAction.onClick}
          loading={secondaryAction.loading}
          disabled={secondaryAction.disabled}
          icon={secondaryAction.icon}
        >
          {secondaryAction.label}
        </Button>
      )}
      {showLoader && (
        <div className="communicationMessageLoaderWrapper">
          <Loader label="" />
        </div>
      )}
    </div>
  );
};
